import { Button, Col, Drawer, Flex, Form, Input, notification, Row, Select, Switch, Upload } from "antd"
import { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import { InboxOutlined, BorderlessTableOutlined, TeamOutlined, UserOutlined } from '@ant-design/icons';
import VotingEnd from "./VotingEnd";
import MeetingAPI from "../../restAPI/MeetingAPI";
import AnnotationAPI from "../../restAPI/AnnotationAPI";
import dayjs from "dayjs";

const { TextArea } = Input;
const { Dragger } = Upload;

const shareOptions = [
    {
        value: 'all',
        label: (
            <span>
                <TeamOutlined style={{ marginRight: 8 }} />
                All
            </span>
        ),
    },
    {
        value: 'pending',
        label: (
            <span>
                <UserOutlined style={{ marginRight: 8 }} />
                Pending
            </span>
        ),
    },
    {
        value: 'individual',
        label: (
            <span>
                <BorderlessTableOutlined style={{ marginRight: 8 }} />
                Individual
            </span>
        ),
    },
];

const allowedFileTypes = [
    'application/pdf',
    // 'application/vnd.ms-excel',
    // 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    // 'text/csv',
    // 'application/vnd.ms-powerpoint',
    // 'application/msword',
    // 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
];



export default function AddNewDocument(props) {
    const { actionState, committee, agenda, meetingCommonInfo } = useSelector(state => state.meeting);
    const [form] = Form.useForm();
    const [fileList, setFileList] = useState([]);
    const [tempFileList, setTempFileList] = useState([]);
    const { companyId, token } = useSelector(state => state.auth);
    const [meetingParticipants, setMeetingParticipants] = useState([]);
    const [shareWithType, setShareWithType] = useState('all')
    const [uploading, setUploading] = useState(false);
    const [uploadFileDetails, setUploadFileDetails] = useState(null);
    const [api, contextHolder] = notification.useNotification();
    
    // FIX: Add upload state tracking to prevent multiple uploads
    const [uploadState, setUploadState] = useState({
        isUploading: false,
        isCompleted: false,
        uploadId: null,
        error: null
    });

    // FIX: Add ref to track if upload is in progress
    const uploadInProgressRef = useRef(false);

    useEffect(() => {
        if (props.newDocOpenAction && props.addNewDocumentDrawerOpenState) {
            setMeetingParticipants(committee.participant.members?.map(member => ({
                label: member.name,
                value: member.userDetailsId
            })) || []);

            if (props.newDocOpenAction !== 'add' && props.selectedAttachmentDetails) {
                const { name, comment, shareWith, voting, file, fileName } = props.selectedAttachmentDetails;
                form.setFieldsValue({
                    name: name || 'N/A',
                    comment: comment || 'N/A',
                    shareWith: shareWith.type || 'All',
                    members: shareWith.members,
                })
                setShareWithType(shareWith.type)
                props.setAllowVoting(voting.isEnabled)
                props.setVotingData({
                    count: voting?.count,
                    end: {
                        customDate: voting?.end?.customDate,
                        type: voting?.end?.type
                    },
                    isEnabled: voting?.isEnabled
                });
                setFileList([
                    {
                        uid: `${Date.now()}`,
                        name: fileName,
                        status: 'done',
                        url: file || '',
                    }
                ]);
                setTempFileList([]);
            }
        }
    }, [props.newDocOpenAction, props.selectedAttachmentDetails, props.addNewDocumentDrawerOpenState])

    const openNotificationWithIcon = (type, message, description, placement) => {
        api[type]({
            message: message,
            description: description,
            placement
        });
    };

    const handleShareWithChange = (value) => {
        setShareWithType(value);
        form.resetFields(['members'])
    }

    const handleDocumentAdd = async () => {
        try {
            await form.validateFields();

            // FIX: Prevent multiple uploads by checking upload state
            if (uploadInProgressRef.current || uploadState.isUploading) {
                openNotificationWithIcon('warning', 'Upload in progress', 'Please wait for the current upload to complete.', 'bottomLeft');
                return;
            }

            // FIX: Only trigger upload if we have files and no upload details, and not already completed
            if (fileList.length > 0 && !uploadFileDetails && !uploadState.isCompleted && props.newDocOpenAction === 'add') {
                console.log('Processing file upload through annotation system...');
                
                // FIX: Set upload state to prevent multiple calls
                uploadInProgressRef.current = true;
                setUploadState(prev => ({ ...prev, isUploading: true, error: null }));
                
                try {
                    await handleUpload();
                    // FIX: Mark upload as completed
                    setUploadState(prev => ({ ...prev, isCompleted: true, isUploading: false }));
                } catch (error) {
                    // FIX: Reset upload state on error
                    setUploadState(prev => ({ ...prev, isUploading: false, error: error.message }));
                    throw error;
                } finally {
                    uploadInProgressRef.current = false;
                }
            }

            if ((fileList.length === 0 || uploadFileDetails === null) && props.newDocOpenAction === 'add') {
                openNotificationWithIcon('error', 'Document adding failed', 'Please upload a file before submitting.', 'bottomLeft');
                throw new Error("Please upload a file before submitting.");

            } else if (props.newDocOpenAction === 'add') {
                const { name, comment, shareWith, members } = form.getFieldsValue();
                const randomId = Math.random().toString(36).substr(2, 9);
                props.setDocumentArray(prev => {
                    const maxOrder = prev.length > 0
                        ? Math.max(...prev.map((item) => item.order || 0))
                        : 0
                    return [
                        ...prev,
                        {
                            _id: null,
                            id: randomId,
                            name,
                            comment: comment ? comment : null,
                            order: maxOrder + 1,
                            file: uploadFileDetails?.savedFileName ? uploadFileDetails?.savedFileName : 'N/A',
                            fileName: uploadFileDetails.originalFileName,
                            shareWith: {
                                type: shareWith ? shareWith : 'all',
                                members: members?.length > 0 ? members : [],
                            },
                            eSignature: {
                                members: []
                            },
                            created: dayjs().format('YYYY-MM-DD'),
                            voting: props.votingData,
                            isPdf: uploadFileDetails?.savedFileName ? uploadFileDetails?.savedFileName.split('.').pop().toLowerCase() === 'pdf' : false,
                            isAddedToDB: false
                        }
                    ]
                })
                openNotificationWithIcon('success', 'Document added', 'Succefully added a new document.', 'bottomLeft');
                resetForm();
            } else {
                const attachmentIndex = props.documentArray.findIndex(item =>
                    item.id == props.selectedAttachmentDetails.id
                )
                const { name, comment, shareWith, members } = form.getFieldsValue();
                const { id, file, fileName, order = 1 } = props.selectedAttachmentDetails;
                const updatedAttachment = {
                    _id: id,
                    id: id,
                    name,
                    comment,
                    file: uploadFileDetails?.savedFileName ? uploadFileDetails?.savedFileName : file,
                    fileName: uploadFileDetails?.originalFileName ? uploadFileDetails?.originalFileName : fileName,
                    shareWith: {
                        type: shareWith ? shareWith : 'all',
                        members: members?.length > 0 ? members : [],
                    },
                    eSignature: {
                        members: []
                    },
                    created: dayjs().format('YYYY-MM-DD'),
                    voting: props.votingData,
                    order: order ? order : 1,
                    isPdf: true,
                    isAddedToDB: false
                }

                if (attachmentIndex !== -1) {
                    const updatedAttachments = props.documentArray.map((item, index) =>
                        index === attachmentIndex ? updatedAttachment : item
                    )
                    openNotificationWithIcon('success', 'Successful', `Attachment details updated successfully.`, 'bottomLeft');
                    props.setDocumentArray(updatedAttachments);
                    resetForm();
                }
                else {
                    openNotificationWithIcon('error', 'Update Failed', `Attachment details update failed.`, 'bottomLeft');
                }
            }
        } catch (errorInfo) {
            console.log('Validation Failed:', errorInfo);
        }
    };

    const uploadProps = {
        name: 'file',
        multiple: false, // FIX: Ensure only single file upload
        beforeUpload: (file) => {
            const isAllowedFileType = allowedFileTypes.includes(file.type);
            if (!isAllowedFileType) {
                openNotificationWithIcon('error', 'Upload failed', 'Only PDF files are allowed.', 'bottomLeft');
                return Upload.LIST_IGNORE;
            }

            // FIX: Prevent multiple file uploads
            if (fileList.length > 0) {
                openNotificationWithIcon('error', 'Upload failed', 'Please remove the existing file before uploading a new one.', 'bottomLeft');
                return Upload.LIST_IGNORE;
            }

            // FIX: Check if upload is in progress
            if (uploadInProgressRef.current || uploading) {
                openNotificationWithIcon('warning', 'Upload in progress', 'Please wait for the current upload to complete.', 'bottomLeft');
                return Upload.LIST_IGNORE;
            }

            // FIX: Reset upload state when new file is selected
            setUploadState({
                isUploading: false,
                isCompleted: false,
                uploadId: null,
                error: null
            });

            setFileList([file]);
            setTempFileList([file]);
            return false;
        },
        onRemove: (file) => {
            setFileList((prev) => prev.filter((item) => item.uid !== file.uid))
            setTempFileList((prev) => prev.filter((item) => item.uid !== file.uid))
            // FIX: Reset upload state when file is removed
            setUploadState({
                isUploading: false,
                isCompleted: false,
                uploadId: null,
                error: null
            });
            setUploadFileDetails(null);
        },
        onDrop(e) {
            console.log('Dropped files', e.dataTransfer.files);
        },
        itemRender: (originNode, file) => {
            if (actionState === 'view' || props.newDocOpenAction === 'view') {
                return (
                    <div>
                        <span>{file.name}</span>
                    </div>
                )
            }
            return originNode;
        }
    };

    const handleUpload = async (e) => {        
        if (uploadInProgressRef.current || uploading) {
            console.log('Upload already in progress, skipping...');
            return;
        }

        setUploading(true);
        uploadInProgressRef.current = true;

        try {
                                                  
                if (fileList.length === 0) {
                    throw new Error('No files to upload');
                }

                const file = fileList[0]; 
                console.log('Uploading file to annotation system:', file.name);
                                
                const attachmentId = AnnotationAPI.generateAttachmentId();
                console.log('Generated attachmentId:', attachmentId);
                console.log('Annotation API URL:', AnnotationAPI.getBaseUrl());
                                
                const meetingId = meetingCommonInfo?.meetingId || null;
                const meetingDate = meetingCommonInfo?.meetingDate || null;                
                const originalDate = meetingCommonInfo?.originalDate 
                    ? new Date(meetingCommonInfo.originalDate).toUTCString()
                    : null;
                
                const annotationResponse = await AnnotationAPI.uploadPdfToAnnotation(
                    file, 
                    attachmentId, 
                    props.currentCompanyInfo?.userDetailsId || 'default-user',
                    token,
                    companyId,
                    meetingId,
                    meetingDate,
                    originalDate
                );
                
                if (!annotationResponse.success) {
                    throw new Error(`Failed to upload ${file.name} to annotation system`);
                }

                const {annotationUrl, session:{name}} = annotationResponse;
              
                openNotificationWithIcon('success', 'Upload success', 'Document uploaded successfully with annotation capabilities.', 'bottomLeft');
                setUploadFileDetails( { originalFileName: name, savedFileName:annotationUrl });
                setTempFileList([]);
                                           
        } catch (error) {
            console.error('Error in file upload:', error);
            openNotificationWithIcon('error', 'Upload failed', `Document upload failed: ${error.message}`, 'bottomLeft');            
            setUploadState(prev => ({ ...prev, isUploading: false, error: error.message }));
        } finally {
            setUploading(false);
            uploadInProgressRef.current = false;
        }
    }

    const handleVotingSwitch = (checked) => {
        props.setAllowVoting(checked);
    }

    useEffect(() => {
        if (!props.allowVoting) {
            props.setVotingData({
                isEnabled: props.allowVoting,
                end: {
                    type: 'after',
                    customDate: null,
                }
            })
        }
    }, [props.allowVoting])

    const resetForm = () => {
        form.resetFields();
        setFileList([]);
        setTempFileList([]);
        setShareWithType('all');
        props.addNewDocDrawerClose();
        setUploadFileDetails(null);
        // FIX: Reset upload state when form is reset
        setUploadState({
            isUploading: false,
            isCompleted: false,
            uploadId: null,
            error: null
        });
        uploadInProgressRef.current = false;
    };

    return (
        <>
            <Drawer
                size="large"
                title="Add new document"
                onClose={() => { resetForm() }}
                open={props.addNewDocDrawerOpen}
                footer={[
                    <Flex justify='flex-end' align='center' gap='large'>
                        <Button onClick={() => { props.addNewDocDrawerClose() }}>
                            Cancel
                        </Button>

                        <Button type="primary" onClick={handleDocumentAdd}>
                            {props.newDocOpenAction === 'view' ? "Close" : props.newDocOpenAction === 'edit' ? "Update" : "Save"}
                        </Button>
                    </Flex>
                ]
                }
            >
                <Form layout="vertical" form={form}>
                    {/* {props.agendaItemOpenState !== 'view' && */}
                    <Col span={24}>
                        <Form.Item label="Document Name" name="name"
                            rules={[
                                {
                                    required: true,
                                    message: 'Please enter the document name',
                                },
                                {
                                    max: 100,
                                    message: 'Document name cannot exceed 100 characters',
                                },
                            ]}
                        >
                            <Input
                                disabled={props.newDocOpenAction === 'view' || actionState === 'view'}
                            />
                        </Form.Item>
                    </Col>
                    {/* } */}
                    {/* {props.agendaItemOpenState !== 'view' &&  */}
                    <Col span={24}>
                        <Form.Item label="Comment" name="comment"
                            rules={[
                                {
                                    max: 250,
                                    message: 'Comment cannot exceed 250 characters',
                                },
                            ]}
                        >
                            <TextArea
                                disabled={props.newDocOpenAction === 'view' || actionState === 'view'}
                            />
                        </Form.Item>
                    </Col>
                    {/* } */}
                    <Col span={24}>
                        <Form.Item label="Share With" name="shareWith" rules={[
                            {
                                required: true,
                                message: 'Please select one option',
                            },
                        ]}>
                            <Select
                                style={{
                                    width: '100%',
                                }}
                                placeholder="Select Category to share with"
                                onChange={handleShareWithChange}
                                options={shareOptions}
                                disabled={props.newDocOpenAction === 'view'}
                            />
                        </Form.Item>
                    </Col>

                    {shareWithType === 'individual' && <Col span={24}>
                        <Form.Item label="Select Individuals" name="members" rules={[{ required: true, message: 'Please select at least one member' }]}>
                            <Select
                                mode="tags"
                                style={{
                                    width: '100%',
                                }}
                                placeholder="Select Category to share with"
                                options={meetingParticipants}
                                disabled={props.newDocOpenAction === 'view'}
                            />
                        </Form.Item>
                    </Col>}

                    {/* {props.agendaItemOpenState !== 'view' &&  */}
                    <Col span={24}>
                        <Form.Item label="Document" name="file">
                            <Dragger {...uploadProps} fileList={fileList} disabled={actionState === 'view' || props.newDocOpenAction === 'view'}>
                                <p className="ant-upload-drag-icon">
                                    <InboxOutlined />
                                </p>
                                <p className="ant-upload-text">Click or drag a file to this area to upload</p>
                            </Dragger>
                        </Form.Item>
                    </Col>
                    {/* } */}
                    {/* {props.agendaItemOpenState !== 'view' &&  */}
                    <Col span={24} style={{ marginBottom: 24 }}>
                        <Flex align='center' justify='flex-end'>
                            <Button
                                type="primary"
                                onClick={(e) => { handleUpload(e) }}
                                disabled={uploading || props.newDocOpenAction === 'view' || (tempFileList.length === 0)}
                                loading={uploading}
                            >
                                Upload
                            </Button>
                        </Flex>
                    </Col>
                    {/* } */}
                    <Form.Item>
                        <p>
                            Allow Voting <Switch 
                                size="small"
                                style={{marginLeft:'15px'}} 
                                onChange={handleVotingSwitch} 
                                checked={props.allowVoting} 
                                disabled={props.newDocOpenAction === 'view'} />
                        </p>
                        {
                            props.allowVoting && <div>
                                <Row>
                                    <Col span={12}>
                                        <Form.Item label="Voting Ends">
                                            <VotingEnd
                                                setVotingData={props.setVotingData}
                                                votingData={props.votingData}
                                                allowVoting={props.allowVoting}
                                                drawerOpenAction={props.newDocOpenAction}
                                                agendaItemOpenState={props.agendaItemOpenState}
                                            />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </div>
                        }
                    </Form.Item>
                </Form>
            </Drawer>
            {contextHolder}
        </>
    )
}